<template>
  <screen-swiper @swiped-right="nextLesson" @swiped-left="prevLesson" :left="canGoBack" right>
    <vv-screen-wrapper>
      <div class="screen-container" v-if="lesson">
        <mq-layout :mq="['desktop']">
          <div class="spacer bread">
            <content-breadcrumb :current="lesson.id" :crumbs="crumbs" class="pl-2" />
          </div>
        </mq-layout>
        <simple-header :label="$t('lesson.previous_lesson')" @click="prevLesson" />

        <h1 class="title" @click="backToMasterclass">
          {{ theme ? theme.title : '' }}
          <br />
          {{ lesson ? lesson.title : '' }}
        </h1>

        <div class="spacer">
          <lesson-progress-bar class="progress-bar" />
        </div>


        <div class="offline-switch-wrapper">
          <vv-switch
            :value="keepOffline"
            v-model="offline"
            :label="$t('lesson.offline_switch')"
          />
        </div>
        <div
          v-if="lesson.videoUrl"
          class="spacer"
          :class="[$mq === 'desktop' ? 'video-spacer-desktop' : 'video-spacer', offline && noOfflineVideo ? 'no-offline' : '']"
        >
          <div class="video">
            <div class="offline-container" v-if="keepOffline">
              <video v-if="!noOfflineVideo" :src="offlineSourceVideo" controls="true" />
              <span v-else>No offline video available</span>
            </div>
            <iframe
              v-else
              class="video-ifr"
              :src="lesson.videoUrl"
              frameborder="0"
              allow="autoplay; fullscreen"
              allowfullscreen
            ></iframe>
          </div>
        </div>
        <div
          v-if="lesson.podcastUrl"
          class="spacer"
          :class="[$mq === 'desktop' ? 'podcast-spacer-desktop' : 'podcast-spacer', offline && noOfflineVideo ? 'no-offline' : '']"
        >
          <div class="podcast">
            <div class="offline-container" v-if="keepOffline">
              <audio v-if="!noOfflinePodcast" :src="offlineSourcePodcast" controls="true" />
              <span v-else>No offline podcast available</span>
            </div>
            <anchor v-else :url="lesson.podcastUrl" @propagate-offline-download="updatePodcastTrackInfos"></anchor>
          </div>
        </div>
        <div class="spacer">
          <vv-rich-text :document="lesson.content" />
        </div>
        <div class="cta">
          <vv-button @click="nextLesson">{{ lesson.buttonLabel || $t('lessonStep.next') }}</vv-button>
        </div>
        <div v-if="lesson && lesson.mustKnow && lesson.mustKnow.length > 0" class="spacer">
          <vv-tips :title="lesson.mustKnowTitle" :tips="lesson.mustKnow"></vv-tips>
        </div>
        <div v-if="lesson && lesson.toRemember && lesson.toRemember.length > 0" class="spacer">
          <vv-tips :title="lesson.toRememberTitle" :tips="lesson.toRemember"></vv-tips>
        </div>
      </div>
    </vv-screen-wrapper>
  </screen-swiper>
</template>

<script>
import { mapGetters } from 'vuex'

import VisiteMap from '@/components/Geoloc/VisiteMap.vue';
import { VvButton, VvRichText, VvTips, VvScreenWrapper, VvSwitch } from '@/components/common'
import SimpleHeader from '@/components/navigation/SimpleHeader'
import Anchor from '@/components/podcast/Anchor'
import ContentBreadcrumb from '@/components/navigation/ContentBreadcrumb'
import ScreenSwiper from '@/components/layout/ScreenSwiper'
import LessonProgressBar from '@/components/lesson/LessonProgressBar'
import VvBackButton from '@/components/common/VvBackButton'
import userMixin from '@/mixins/user'
import remoteConfigMixin from '@/mixins/remoteConfig'
import { REGISTER_COMPLETION } from '@/store/modules/completion/types'
import { SET_KEEP_OFFLINE_MODE } from '@/store/modules/user/types'
import VideoManager from '@/lib/video'
import PodcastManager from '@/lib/podcast'
import VvModal from '@/components/common/VvModal.vue'

import { goToNextLessonScreen, goToPrevLessonScreen } from '@/helpers/navigation'

const moment = require('moment');

export default {
  name: 'Lesson',
  components: {
    VvBackButton,
    LessonProgressBar,
    VvButton,
    VvRichText,
    VvTips,
    ScreenSwiper,
    VvScreenWrapper,
    SimpleHeader,
    VvSwitch,
    ContentBreadcrumb,
    Anchor,
    VvModal,
    VisiteMap
  },
  data() {
    return {
      offline: false,
      offlineSourceVideo: null,
      offlineSourcePodcast: null,
      noOfflineVideo: false,
      noOfflinePodcast: false,
      podcastTrackUrl: null,
      podcastTrackId: null,
      timeStart: null,
      timeStop: null,
      seen: false
    }
  },
  mixins: [userMixin, remoteConfigMixin],
  computed: {
    ...mapGetters({
      lessonStepProgression: 'lessonStepProgression',
      firebaseId: 'lessonFirebaseID',
      lesson: 'currentLesson',
      lessonStep: 'currentLessonStep',
      theme: 'currentTheme'
    }),
    canGoBack() {
      if (!this.lesson) return false

      return this.lessonStep.lessons.indexOf(this.lesson.id) > 0
    },
    crumbs() {
      if (this.theme && this.lessonStep && this.lesson) {
        return [
          {
            label: this.theme.title,
            value: this.theme.id,
            routing: { name: 'theme', params: { themeId: this.theme.id } }
          },
          {
            label: this.lessonStep.title,
            value: this.lessonStep.id,
            routing: {
              name: 'theme',
              params: { themeId: this.theme.id }
            }
          },
          {
            label: this.lesson.title,
            value: this.lesson.id
          }
        ]
      }
      
      return []
    },
    keepOffline() {
      return this.$store.state.user.keepOfflineMode
    }
  },
  created() {
    this.offline = this.keepOffline
  },
  async mounted() {
    this.prepareOfflineVideo()
    this.prepareOfflinePodcast()
    this.timeStart = moment().format("X")
    // track lesson infos
    // this.$gtm.trackEvent({
    //   event: 'start lesson', // Event type [default = 'interaction'] (Optional)
    //   category: 'Masterclass',
    //   action: 'click',
    //   label: 'Start lesson',
    //   value: {
    //     userId: this.userId,
    //     themeId: this.theme.id,
    //     lessonStepId: this.lessonStep.id,
    //     lessonId: this.lesson.id,
    //     timeStarted: moment().format()
    //   },
    //   noninteraction: false // Optional
    // });

    if (localStorage.getItem('triggerModal')) {
      this.showModal()
      localStorage.removeItem('triggerModal')
    }
  },
  methods: {
    showModal() {
      this.$modal.show(
        VvModal,
        {},
        {
          adaptive: true,
          reset: true,
          styles: {
            'border-radius': '20px',
            }
        }
      )
    },
    updatePodcastTrackInfos(value) {
      this.podcastTrackUrl = value.url
      this.podcastTrackId = value.id
    },
    prevLesson() {
      goToPrevLessonScreen(this.lesson.id, this.lessonStep.id, this.theme.id)
    },
    nextLesson() {
      this.$store.dispatch(REGISTER_COMPLETION, {
        contentType: 'lesson',
        id: this.lesson.id,
        status: { complete: true }
      })
      this.timeEnd = moment().format("X")
      // track next lesson
      this.$gtm.trackEvent({
        event: 'Variables - Lesson time', // Event type [default = 'interaction'] (Optional)
        category: 'Masterclass',
        action: 'click',
        label: 'Next lesson',
        value: {
          userId: this.userId,
          themeId: this.theme.id,
          lessonStepId: this.lessonStep.id,
          lessonId: this.lesson.id,
          time: this.timeEnd - this.timeStart
        },
        noninteraction: false // Optional
      });

      goToNextLessonScreen(this.lesson.id, this.lessonStep.id, this.theme.id, true)
    },
    async prepareOfflineVideo(newLesson) {
      const lesson = newLesson || this.lesson

      this.noOfflineVideo = true
      this.offlineSourceVideo = null

      try {
        const videoId = VideoManager.getVimeoIdFromLesson(lesson)

        const blob = await VideoManager.getVideo(videoId)

        this.offlineSourceVideo = URL.createObjectURL(blob)
        this.noOfflineVideo = false
      } catch (error) {
        this.noOfflineVideo = true
      }
    },
    async prepareOfflinePodcast(newLesson) {
      const lesson = newLesson || this.lesson
      let blob = null
      this.noOfflinePodcast = true
      this.offlineSourcePodcast = null

      try {
        // download podcast
        blob = await PodcastManager.dispatchPodcastDownload(this.lesson.podcastUrl, this.podcastTrackUrl, this.podcastTrackId, false)
        if (blob) {
          // set sources
          this.offlineSourcePodcast = URL.createObjectURL(blob)
          this.noOfflinePodcast = false
        } else {
          this.noOfflinePodcast = true
        }
      } catch (error) {
        this.noOfflinePodcast = true
      }
    },
    backToMasterclass() {
      const { themeId } = this.$route.params

      this.$router.push({ name: 'theme', params: { themeId } })
    }
  },
  watch: {
    async lesson(newLesson) {
      this.prepareOfflineVideo(newLesson)
      this.prepareOfflinePodcast(newLesson)
    },
    async podcastTrackUrl(newDownloadUrl) {
      this.prepareOfflinePodcast()
    },
    offline(value) {
      this.$store.commit(SET_KEEP_OFFLINE_MODE, value)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';
@import '@/assets/styles/responsive.scss';

.screen-container {
  padding-top: 0px;
}

.offline-switch-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.video-spacer, .podcast-spacer {
  max-width: 350px;
  margin: 0 auto;
}

.video-spacer-desktop, .podcast-spacer-desktop {
  margin: 0 auto;
}


.video {
  border-radius: 6px;
  position: relative;
  overflow: hidden;
  height: 50vh;
}

.video iframe,
.video object,
.video embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
}

.video-spacer .video {
  height: 35vh;
}

.podcast iframe,
.podcast object,
.podcast embed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  overflow: hidden;
}

.video, .podcast {
  .offline-container {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    video {
      max-width: 100%;
      height: 100%;
    }
  }
}

.title {
  font-size: 14px;
  font-weight: 900;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: $primary;
  cursor: pointer;
}

.spacer {
  margin-bottom: 20px;

  &.bread {
    margin-top: 30px;
    margin-bottom: 44px;
    position: relative;
    
    @include for-size(desktop-up) {
      left: -30px;
    }
  }
}

.cta {
  bottom: 10px !important;

  // Comment to get old values for padding 10px/38px
  button {
    padding: 12px 50px !important;
  }
}

.no-offline {
  height: unset;

  .video, .podcast {
    height: unset;
  }
}

.scan-button {
  position: absolute;
  border: none;
  outline: none;
  background: none;
  border-radius: 4px;
  right: 85px;
  margin-top: 10px;
}
.scan-button:hover {
  cursor: pointer;
}
@media screen and (max-width: 420px) {
  .scan-button {
    right: 55px;
    img {
      width: 40px;
    }
  }
}
@media screen and (min-width: 1281px) {
  .scan-button {
    display: none;
  }
}
.geoloc-button {
  position: absolute;
  border: none;
  outline: none;
  background: none;
  border-radius: 4px;
  right: 10px;
  margin-top: 10px;
}
.geoloc-button:hover {
  cursor: pointer;
}
@media screen and (max-width: 420px) {
  .geoloc-button {
    right: 5px;
    img {
      width: 40px;
    }
  }
}
</style>
